import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Grid,
  InputAdornment,
  Stack,
} from '@mui/material';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import {
  fetchFolderSettings,
  addFolder,
  updateFolder,
  deleteFolder,
} from '../services/folderService';
import { createApiClient } from '../services/apiClient';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SettingsIcon from '@mui/icons-material/Settings';
import FolderIcon from '@mui/icons-material/Folder';
import StorageIcon from '@mui/icons-material/Storage';
import GroupIcon from '@mui/icons-material/Group';
import UserManagement from '../components/UserManagement';
import { importPatients, clearAllPatients, exportAllPatients } from '../services/patientService';
import { downloadBackup, restoreDatabase, downloadMigrationPackage } from '../services/databaseService';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BackupIcon from '@mui/icons-material/Backup';
import RestoreIcon from '@mui/icons-material/Restore';
import LinkIcon from '@mui/icons-material/Link';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { getServerSettings, setServerSettings, clearServerSettings, API_BASE_URL as CURRENT_API_URL, APP_VERSION } from '../config';
import axios from 'axios';

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const DiagnosisManagement = ({ apiClient }) => {
  const { t } = useTranslation();
  const [diagnoses, setDiagnoses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDiag, setEditingDiag] = useState(null);
  const [diagName, setDiagName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadDiagnoses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/system/epembaseis');
      setDiagnoses(response.data);
    } catch (err) {
      console.error('Failed to load diagnoses:', err);
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    loadDiagnoses();
  }, [loadDiagnoses]);

  const handleOpenDialog = (diag = null) => {
    setEditingDiag(diag);
    setDiagName(diag ? diag.epembasi : '');
    setDialogOpen(true);
    setError('');
  };

  const handleSave = async () => {
    if (!diagName.trim()) return;
    setSaving(true);
    setError('');
    try {
      if (editingDiag) {
        await apiClient.put(`/system/epembaseis/${editingDiag.id_ep}`, { epembasi: diagName });
      } else {
        await apiClient.post('/system/epembaseis', { epembasi: diagName });
      }
      setDialogOpen(false);
      loadDiagnoses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save diagnosis');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('Are you sure you want to delete this diagnosis?'))) return;
    try {
      await apiClient.delete(`/system/epembaseis/${id}`);
      loadDiagnoses();
    } catch (err) {
      alert('Failed to delete diagnosis');
    }
  };

  const filteredDiagnoses = diagnoses.filter(d => 
    d.epembasi.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" fontWeight="bold">{t('Manage Master Diagnosis List')}</Typography>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => handleOpenDialog()}>
            {t('Add New')}
          </Button>
        </Stack>

        <TextField
          fullWidth
          placeholder={t('Search diagnoses...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <List sx={{ maxHeight: 500, overflow: 'auto', bgcolor: 'background.paper', borderRadius: 2 }}>
            {filteredDiagnoses.map((diag) => (
              <ListItem key={diag.id_ep} divider>
                <ListItemText primary={diag.epembasi} />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => handleOpenDialog(diag)} sx={{ mr: 1 }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton edge="end" color="error" onClick={() => handleDelete(diag.id_ep)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {filteredDiagnoses.length === 0 && (
              <ListItem><ListItemText primary={t('No diagnoses found')} sx={{ textAlign: 'center', opacity: 0.5 }} /></ListItem>
            )}
          </List>
        )}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{editingDiag ? t('Edit Diagnosis') : t('Add New Diagnosis')}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label={t('Diagnosis Name')}
              fullWidth
              variant="outlined"
              value={diagName}
              onChange={(e) => setDiagName(e.target.value)}
              sx={{ mt: 1 }}
              error={Boolean(error)}
              helperText={error}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>{t('Cancel')}</Button>
            <Button onClick={handleSave} variant="contained" disabled={saving || !diagName.trim()}>
              {saving ? <CircularProgress size={24} /> : t('Save')}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const MAPPABLE_FIELDS = [
  { key: 'first_name', label: 'First Name', required: true },
  { key: 'last_name', label: 'Last Name', required: true },
  { key: 'amka', label: 'AMKA / SSN' },
  { key: 'date_birth', label: 'Date of Birth' },
  { key: 'gender', label: 'Gender' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'address', label: 'Address' },
  { key: 'diagnosis', label: 'Diagnosis' },
  { key: 'notes', label: 'Notes / Comments' },
  { key: 'allergies', label: 'Allergies' },
  { key: 'age', label: 'Age' },
  { key: 'last_visit', label: 'Last Visit Date' },
  { key: 'card_date', label: 'Card Create Date' },
];

const AUTO_MATCH_RULES = {
  first_name: ['First Name', 'ONOMA', 'First_Name', 'FirstName', 'Given Name'],
  last_name: ['Last Name', 'EPITHETO', 'Last_Name', 'LastName', 'Surname'],
  date_birth: ['DOB', 'HMER_GENNHSHS', 'Date of Birth', 'BirthDate', 'Date_Birth'],
  gender: ['Gender', 'FYLO', 'Sex'],
  amka: ['AMKA', 'Social Security', 'SSN'],
  email: ['Email', 'E-mail', 'Mail'],
  phone: ['Phone', 'THLEFONO', 'Telephone'],
  mobile: ['Mobile', 'KINHTO'],
  address: ['Address', 'DIEYTHYNSH'],
  diagnosis: ['Diagnosis', 'DIAGNOSH'],
  notes: ['Notes', 'PARATHRHSEIS'],
  allergies: ['Allergies'],
  age: ['Age', 'HLIKIA'],
  last_visit: ['Last Visit', 'TELEYTAIA_EPISKEPSH'],
  card_date: ['Card Date', 'HMER_KARTAS']
};

const SettingsPage = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, idleTimeoutMinutes, updateIdleTimeout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { mode, toggleDarkMode } = useThemeMode();

  const apiClient = useMemo(() => {
    if (!isAuthenticated) return null;
    const token = typeof window !== 'undefined' ? sessionStorage.getItem('token') : null;
    return token ? createApiClient(token) : null;
  }, [isAuthenticated]);

  const activeTab = useMemo(() => {
    const tabMapping = { general: 0, connection: 1, folders: 2, diagnoses: 3, migration: 4, users: 5, database: 6, system_migration: 7 };
    const rawTab = tabMapping[searchParams.get('tab')] ?? 0;
    
    // Security: Any user can access General and Connection
    // Security: Only admins can access tabs 2, 4, 5, 6, 7. Diagnoses (3) is open to read-write too.
    const isRestricted = rawTab === 2 || rawTab >= 4;
    if (user?.role !== 'admin' && isRestricted) {
      return 0;
    }
    return rawTab;
  }, [searchParams, user?.role]);

  const handleTabChange = (event, newValue) => {
    // We need to map the newValue back to its name, taking into account hidden tabs
    const allTabs = ['general', 'connection', 'folders', 'diagnoses', 'migration', 'users', 'database', 'system_migration'];
    const visibleTabs = allTabs.filter(t => t === 'general' || t === 'connection' || t === 'diagnoses' || user?.role === 'admin');
    setSearchParams({ tab: visibleTabs[newValue] });
  };

  // Folder state
  const [folders, setFolders] = useState([]);
  const [selectedPc, setSelectedPc] = useState(
    () => (typeof window !== 'undefined' ? localStorage.getItem('selectedPc') || '' : '')
  );
  const [newPcName, setNewPcName] = useState('');
  const [folderPath, setFolderPath] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [serverPathPc, setServerPathPc] = useState(
    () => (typeof window !== 'undefined' ? localStorage.getItem('serverPathPc') || '' : '')
  );
  const [localPathPc, setLocalPathPc] = useState(
    () => (typeof window !== 'undefined' ? localStorage.getItem('localPathPc') || '' : '')
  );

  // Delete confirmation dialog
  const [deleteTarget, setDeleteTarget] = useState(null); // folder object to delete

  // Notification preference
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    () => localStorage.getItem('notificationsEnabled') !== 'false'
  );

  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');
  const [availableHeaders, setAvailableHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [showMapping, setShowMapping] = useState(false);

  // Clear all state
  const [confirmClearDialogOpen, setConfirmClearDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [clearing, setClearing] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Database state
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [confirmRestoreDialogOpen, setConfirmRestoreDialogOpen] = useState(false);
  const [confirmRestoreText, setConfirmRestoreText] = useState('');

  // Clinical Hub / Connection state
  const [serverInfo, setServerInfo] = useState(null);
  const [remoteHostInput, setRemoteHostInput] = useState('');
  const [hubSaving, setHubSaving] = useState(false);

  // System Migration Packager state
  const [migrationPkgLoading, setMigrationPkgLoading] = useState(false);

  // Connection settings state
  const [connSettings, setConnSettings] = useState(() => getServerSettings());
  const [connPassword, setConnPassword] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  const [showConnPassword, setShowConnPassword] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadFolders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!apiClient) {
        setLoading(false);
        return;
      }
      const data = await fetchFolderSettings(apiClient);
      setFolders(data);

      const savedPc = localStorage.getItem('selectedPc');
      const savedFolderPath = localStorage.getItem('selectedFolderPath');

      if (savedPc && savedFolderPath) {
        const found = data.find((f) => f.pc_name === savedPc);
        if (found) {
          setSelectedPc(savedPc);
          setFolderPath(savedFolderPath);
          return;
        }
      }

      try {
        const pcName = window.electron?.getPcName?.() || '';
        if (pcName) {
          const currentPcFolder = data.find((f) => f.pc_name === pcName);
          if (currentPcFolder) {
            setSelectedPc(pcName);
            setFolderPath(currentPcFolder.folder);
            localStorage.setItem('selectedPc', pcName);
            localStorage.setItem('selectedFolderPath', currentPcFolder.folder);
          } else {
            setNewPcName(pcName);
          }
        }
      } catch (e) {
        console.warn('Could not get PC name:', e);
      }
    } catch (err) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to load folder settings');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  const loadServerInfo = useCallback(async () => {
    if (window.electron?.getServerInfo) {
      try {
        const info = await window.electron.getServerInfo();
        setServerInfo(info);
        setRemoteHostInput(info.remoteHost || '');
      } catch (err) {
        console.error('Failed to load surgical hub info:', err);
      }
    }
  }, []);

  useEffect(() => {
    loadServerInfo();
  }, [loadServerInfo]);

  // Handle inbound actions from Dashboard (Quick Actions)
  useEffect(() => {
    const action = searchParams.get('action');
    if (!action) return;

    if (action === 'backup' && activeTab === 5 && !backupLoading) {
      handleDownloadBackup();
      // Clean up param
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
    } else if (action === 'export' && activeTab === 3 && !exportLoading) {
      handleExport();
      // Clean up param
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
    } else if (action === 'import' && activeTab === 3) {
      // Just focus on the tab is enough, but we could trigger file input
      const fileInput = document.getElementById('patient-import-input');
      if (fileInput) fileInput.click();
      
      // Clean up param
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, activeTab, backupLoading]);

  const handleUpdateRemoteHost = async () => {
    if (!window.electron?.setRemoteHost) return;
    setHubSaving(true);
    try {
      await window.electron.setRemoteHost(remoteHostInput.trim());
      setSuccess('Clinical Hub settings updated. Please restart the application for changes to take effect.');
      loadServerInfo();
    } catch (err) {
      setError('Failed to update hub connection settings.');
    } finally {
      setHubSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setTestResult(null);
    try {
      // Build the test URL from current inputs
      const testUrl = `${connSettings.protocol}://${connSettings.ip}:${connSettings.port}/api/health`;
      
      const config = {};
      if (connSettings.username && connPassword) {
        config.headers = {
          'Authorization': `Basic ${btoa(`${connSettings.username}:${connPassword}`)}`
        };
      }

      const response = await axios.get(testUrl, config);

      if (response.status === 200) {
        setTestResult({ success: true, message: 'Connection successful! Server reached.' });
      } else {
        setTestResult({ success: false, message: `Server responded with status ${response.status}` });
      }
    } catch (err) {
      setTestResult({ success: false, message: `Failed to reach server: ${err.response?.data?.error || err.message}` });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSaveConnection = () => {
    if (!connSettings.ip) {
      setError('Server IP or Hostname is required.');
      return;
    }
    setServerSettings({
      ...connSettings,
      password: connPassword || undefined
    });
    setSuccess('Connection settings saved. The application will now reload to apply changes.');
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  const handleResetConnection = () => {
    clearServerSettings();
    setSuccess('Connection settings reset to default. Reloading...');
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  const handlePcChange = (pcName) => {
    setSelectedPc(pcName);
    localStorage.setItem('selectedPc', pcName);
    setError('');
    setSuccess('');
    if (!pcName) {
      setFolderPath('');
      return;
    }
    const folder = folders.find((f) => f.pc_name === pcName);
    if (folder) {
      setFolderPath(folder.folder);
      localStorage.setItem('selectedFolderPath', folder.folder);
    } else {
      setFolderPath('');
      localStorage.removeItem('selectedFolderPath');
    }
  };

  const handleAddFolder = async () => {
    if (!apiClient) return setError('Authentication required.');
    if (!newPcName.trim()) return setError('PC name is required');
    if (!folderPath.trim()) return setError('Folder path is required');
    setSaving(true);
    setError('');
    try {
      const data = await addFolder(apiClient, newPcName.trim(), folderPath.trim());
      setFolders((prev) => [...prev, data]);
      setSelectedPc(data.pc_name);
      setNewPcName('');
      setSuccess('Folder added successfully');
    } catch (err) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to add folder');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateFolder = async () => {
    if (!apiClient || !selectedPc) return;
    const folder = folders.find((f) => f.pc_name === selectedPc);
    if (!folder) return;
    setSaving(true);
    setError('');
    try {
      const data = await updateFolder(apiClient, folder.id, {
        pc_name: selectedPc,
        folder: folderPath.trim(),
      });
      setFolders((prev) => prev.map((f) => (f.id === data.id ? data : f)));
      setSuccess('Folder updated successfully');
    } catch (err) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to update folder');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFolder = async (folder) => {
    setDeleteTarget(folder);
  };

  const confirmDelete = async () => {
    if (!apiClient || !deleteTarget) return;
    try {
      await deleteFolder(apiClient, deleteTarget.id);
      setFolders((prev) => prev.filter((f) => f.id !== deleteTarget.id));
      if (selectedPc === deleteTarget.pc_name) {
        setSelectedPc('');
        setFolderPath('');
      }
      setSuccess('Folder deleted successfully');
    } catch (err) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to delete folder');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleBrowseFolder = async () => {
    try {
      const result = await window.electron?.selectFolder?.();
      if (result?.filePaths?.[0]) {
        setFolderPath(result.filePaths[0]);
        setSuccess('');
      }
    } catch (err) {
      setError('Failed to open folder browser: ' + (err.message ?? 'Unknown error'));
    }
  };

  const handleNotificationsToggle = (e) => {
    const val = e.target.checked;
    setNotificationsEnabled(val);
    localStorage.setItem('notificationsEnabled', String(val));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
      setImportError('');
      setImportResult(null);
      setAvailableHeaders([]);
      setMapping({});
      setShowMapping(false);

      const reader = new FileReader();
      const extension = file.name.split('.').pop().toLowerCase();

      reader.onload = (evt) => {
        try {
          let headers = [];
          if (extension === 'csv') {
            const text = evt.target.result;
            const result = Papa.parse(text, { preview: 1 });
            headers = result.data[0] || [];
          } else if (extension === 'xlsx' || extension === 'xls') {
            const data = new Uint8Array(evt.target.result);
            const workbook = XLSX.read(data, { type: 'array', sheetRows: 1 });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            headers = json[0] || [];
          }

          const filteredHeaders = headers.map(h => String(h).trim()).filter(h => h.length > 0);
          setAvailableHeaders(filteredHeaders);
          
          // Auto-mapping
          const savedMapping = JSON.parse(localStorage.getItem('lastImportMapping') || '{}');
          const initialMapping = {};
          
          MAPPABLE_FIELDS.forEach(field => {
            // Priority 1: Was this header used for this field last time AND does it exist now?
            const lastHeader = savedMapping[field.key];
            if (lastHeader && filteredHeaders.includes(lastHeader)) {
              initialMapping[field.key] = lastHeader;
              return;
            }

            // Priority 2: Auto-match rules
            const rules = AUTO_MATCH_RULES[field.key] || [];
            const match = filteredHeaders.find(h => 
              rules.some(r => r.toLowerCase() === h.toLowerCase())
            );
            if (match) initialMapping[field.key] = match;
          });
          setMapping(initialMapping);
          setShowMapping(true);
        } catch (err) {
          console.error('Header parsing error:', err);
          setImportError('Failed to read file headers. Please ensure the file is valid.');
        }
      };

      if (extension === 'csv') {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    }
  };

  const handleMappingChange = (fieldKey, headerValue) => {
    setMapping(prev => {
      const newMapping = {
        ...prev,
        [fieldKey]: headerValue
      };
      // Persist the current mapping choices
      localStorage.setItem('lastImportMapping', JSON.stringify(newMapping));
      return newMapping;
    });
  };

  const handleImport = async () => {
    if (!apiClient || !importFile) return;
    
    // Validate required fields
    const missingFields = MAPPABLE_FIELDS
      .filter(f => f.required && !mapping[f.key])
      .map(f => f.label);
    
    if (missingFields.length > 0) {
      setImportError(`Please map the following required fields: ${missingFields.join(', ')}`);
      return;
    }

    setImportLoading(true);
    setImportError('');
    setImportResult(null);
    try {
      const result = await importPatients(apiClient, importFile, mapping);
      // Persist successful mapping for next time
      localStorage.setItem('lastImportMapping', JSON.stringify(mapping));
      setImportResult(result);
      setImportFile(null);
      setAvailableHeaders([]);
      setMapping({});
      setShowMapping(false);
      const fileInput = document.getElementById('patient-import-input');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      setImportError(err.response?.data?.error ?? err.message ?? 'Failed to import patients');
    } finally {
      setImportLoading(false);
    }
  };

  const handleClearAll = async () => {
    if (!apiClient || confirmText !== 'DELETE ALL PATIENTS') return;
    setClearing(true);
    setError('');
    try {
      await clearAllPatients(apiClient);
      setSuccess('All patient data cleared successfully');
      setConfirmClearDialogOpen(false);
      setConfirmText('');
    } catch (err) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to clear patient data');
    } finally {
      setClearing(false);
    }
  };

  const handleExport = async () => {
    if (!apiClient) return;
    
    setExportLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await exportAllPatients(apiClient);
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      
      // Set the filename
      const filename = `MyEMR_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', filename);
      
      // Append to the document and trigger click
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess('Patient data exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      let msg = 'Failed to export patient data. Make sure you have admin privileges.';
      
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          msg = json.error || msg;
        } catch (e) {}
      } else {
        msg = err.response?.data?.error || msg;
      }
      
      setError(msg);
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadBackup = async () => {
    if (!apiClient) return;
    
    setBackupLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const blob = await downloadBackup(apiClient);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `MyEMR_System_Backup_${new Date().toISOString().split('T')[0]}.sql`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess('System backup downloaded successfully');
    } catch (err) {
      console.error('Backup error:', err);
      let msg = 'Failed to generate system snapshot. Make sure the database tools are configured and your user has the correct permissions.';
      
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          msg = json.details || json.error || msg;
        } catch (e) {}
      } else {
        msg = err.response?.data?.details || err.response?.data?.error || msg;
      }
      
      setError(msg);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.name.toLowerCase().endsWith('.sql')) {
        setRestoreFile(file);
      } else {
        setError('Please select a valid .sql backup file');
      }
    }
  };

  const handleRestoreDatabase = async () => {
    if (!apiClient || !restoreFile || confirmRestoreText !== 'RESTORE SYSTEM') return;
    
    setRestoreLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await restoreDatabase(apiClient, restoreFile);
      setSuccess('Database restored successfully. The system has been reverted.');
      setConfirmRestoreDialogOpen(false);
      setConfirmRestoreText('');
      setRestoreFile(null);
      // Wait a moment then refresh clinical environment
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      console.error('Restore error:', err);
      setError(err.response?.data?.error ?? 'Database restoration failed. This is a critical system operation.');
    } finally {
      setRestoreLoading(false);
    }
  };  const handleDownloadMigrationPackage = async () => {
    if (!apiClient) return;
    
    setMigrationPkgLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const blob = await downloadMigrationPackage(apiClient);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `MyEMR_Migration_Pack_${new Date().toISOString().split('T')[0]}.zip`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess('Migration package generated and downloaded successfully. Transfer this file to your Proxmox VM.');
    } catch (err) {
      console.error('Migration pack error:', err);
      let msg = 'Failed to generate migration package. Large clinical data take time, check server console for logs.';
      setError(msg);
    } finally {
      setMigrationPkgLoading(false);
    }
  };

  const isNewPc = !selectedPc || !folders.some((f) => f.pc_name === selectedPc);
  const hasChanges = Boolean(
    (selectedPc && folders.find((f) => f.pc_name === selectedPc)?.folder !== folderPath) ||
      (isNewPc && newPcName && folderPath)
  );

  return (
    <Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, background: 'linear-gradient(45deg, #1976d2, #64b5f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {t('Settings')}
            </Typography>
            <Chip label="v2.1.0" size="small" color="primary" variant="outlined" sx={{ fontWeight: 800, height: 24 }} />
          </Box>
          <Typography variant="subtitle1" color="textSecondary" gutterBottom>
            {t('Manage your preferences and profile.')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DashboardIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{ minWidth: 130 }}
        >
          {t('Dashboard')}
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="settings tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<SettingsIcon />} label={t('General')} iconPosition="start" />
          <Tab icon={<LinkIcon />} label={t('Server Connection')} iconPosition="start" />
          {user?.role === 'admin' && (
            <Tab icon={<FolderIcon />} label={t('Folders')} iconPosition="start" />
          )}
          <Tab icon={<MedicalInformationIcon />} label={t('Diagnoses')} iconPosition="start" />
          {user?.role === 'admin' && (
            <Tab icon={<StorageIcon />} label={t('Migration')} iconPosition="start" />
          )}
          {user?.role === 'admin' && (
            <Tab icon={<GroupIcon />} label={t('Users')} iconPosition="start" />
          )}
          {user?.role === 'admin' && (
            <Tab icon={<StorageIcon />} label={t('Database')} iconPosition="start" />
          )}
          {user?.role === 'admin' && (
            <Tab icon={<BackupIcon />} label={t('System Migration')} iconPosition="start" />
          )}
        </Tabs>
      </Box>

      {/* Tab 0: General */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Preferences */}
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                {t('Preferences')}
              </Typography>
              <List disablePadding>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={t('Dark Mode')}
                    secondary={t('Switch between light and dark interface')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={mode === 'dark'}
                        onChange={toggleDarkMode}
                        color="primary"
                      />
                    }
                    label={mode === 'dark' ? t('On') : t('Off')}
                    labelPlacement="start"
                    sx={{ mr: 0 }}
                  />
                </ListItem>
                <Divider component="li" />
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={t('Patient Change Notifications')}
                    secondary={t('Receive notifications when patient records are modified')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationsEnabled}
                        onChange={handleNotificationsToggle}
                        color="primary"
                      />
                    }
                    label={notificationsEnabled ? t('On') : t('Off')}
                    labelPlacement="start"
                    sx={{ mr: 0 }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Session & Security */}
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                {t('Session & Security')}
              </Typography>
              <List disablePadding>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={t('Idle Session Timeout')}
                    secondary={t('Automatically sign out after a period of inactivity')}
                  />
                  <FormControl sx={{ minWidth: 150 }}>
                    <Select
                      size="small"
                      value={idleTimeoutMinutes}
                      onChange={(e) => updateIdleTimeout(e.target.value)}
                    >
                      <MenuItem value={5}>5 {t('Minutes')}</MenuItem>
                      <MenuItem value={10}>10 {t('Minutes')}</MenuItem>
                      <MenuItem value={20}>20 {t('Minutes')}</MenuItem>
                      <MenuItem value={30}>30 {t('Minutes')}</MenuItem>
                      <MenuItem value={60}>60 {t('Minutes')}</MenuItem>
                    </Select>
                  </FormControl>
                </ListItem>
                <Divider component="li" />
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={t('Sign Out on Tab Close')}
                    secondary={t('Always active for clinical data security')}
                  />
                  <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                    {t('Active')}
                  </Typography>
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Clinical Hub & Connectivity */}
          {window.electron?.isElectron && (
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'primary.light', borderRadius: 3, bgcolor: 'primary.main', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <StorageIcon />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Clinical Hub & Connectivity
                  </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', p: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ opacity: 0.9, mb: 1, fontWeight: 700 }}>
                        THIS MACHINE'S IDENTITY
                      </Typography>
                      {serverInfo ? (
                        <>
                          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                            {serverInfo.localIp}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Use this IP address on other workstations to connect to this Hub.
                          </Typography>
                          {!serverInfo.remoteHost && (
                            <Box sx={{ mt: 1, display: 'inline-block', bgcolor: 'success.main', px: 1, py: 0.3, borderRadius: 1, fontSize: '0.7rem', fontWeight: 700 }}>
                              HUB ACTIVE
                            </Box>
                          )}
                        </>
                      ) : (
                        <CircularProgress size={20} color="inherit" />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', p: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ opacity: 0.9, mb: 1, fontWeight: 700 }}>
                        REMOTE HUB CONNECTION
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g. 192.168.1.50 or myemr.mooo.com"
                        value={remoteHostInput}
                        onChange={(e) => setRemoteHostInput(e.target.value)}
                        sx={{ 
                          bgcolor: 'white', 
                          borderRadius: 1,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'transparent' },
                            '&:hover fieldset': { borderColor: 'transparent' },
                            '&.Mui-focused fieldset': { borderColor: 'transparent' },
                          }
                        }}
                      />
                      <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                          variant="contained" 
                          color="secondary" 
                          size="small"
                          disabled={hubSaving}
                          onClick={handleUpdateRemoteHost}
                          sx={{ fontWeight: 700 }}
                        >
                          {hubSaving ? 'Saving...' : 'Connect to Hub'}
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                
                <Typography variant="caption" sx={{ display: 'block', mt: 2, opacity: 0.8, fontStyle: 'italic' }}>
                  Note: Connecting to a remote hub will disable the local database on this workstation.
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Profile */}
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                {t('Profile')}
              </Typography>
              <List disablePadding>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText 
                    primary={t('Logged in as')} 
                    secondary={user?.name || user?.username || t('Not available')} 
                    secondaryTypographyProps={{ fontWeight: 700, color: 'primary.main', fontSize: '1rem' }}
                  />
                </ListItem>
                <Divider component="li" />
                <ListItem sx={{ px: 0 }}>
                  <ListItemText 
                    primary={t('Account Description')} 
                    secondary={
                      user?.role === 'admin' 
                        ? t('Full access — read, write, delete, manage users')
                        : user?.role === 'read-write'
                        ? t('Can add & edit patients, cannot delete')
                        : t('Read-only access to all patient data')
                    } 
                  />
                </ListItem>
                <Divider component="li" />
                <ListItem sx={{ px: 0 }}>
                  <ListItemText primary={t('Email')} secondary={user?.email ?? t('Not available')} />
                </ListItem>
                <Divider component="li" />
                <ListItem sx={{ px: 0 }}>
                  <ListItemText 
                    primary={t('Role')} 
                    secondary={
                      <Chip 
                        label={
                          user?.role === 'admin' 
                            ? t('Administrator') 
                            : user?.role === 'read-write' 
                            ? t('User') 
                            : t('Viewer')
                        } 
                        size="small" 
                        color={user?.role === 'admin' ? 'error' : user?.role === 'read-write' ? 'warning' : 'default'}
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    } 
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* About System & Version */}
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                {t('About System')}
              </Typography>
              <List disablePadding>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={t('Application Version')}
                    secondary={t('MySurgeon Clinical EMR Suite')}
                  />
                  <Chip label={`v${APP_VERSION}`} color="primary" sx={{ fontWeight: 800 }} />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      {/* Tab 1: Server Connection */}
      <TabPanel value={activeTab} index={1}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <LinkIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t('Server Connection Settings')}
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('Configure the primary surgical hub location. These settings are stored locally on this device and are used to reach the clinical database server on your network.')}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>{t('Protocol')}</InputLabel>
                    <Select
                      value={connSettings.protocol}
                      label={t('Protocol')}
                      onChange={(e) => setConnSettings(prev => ({ ...prev, protocol: e.target.value }))}
                    >
                      <MenuItem value="http">{t('HTTP (Standard)')}</MenuItem>
                      <MenuItem value="https">{t('HTTPS (Secure)')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    label={t('Server IP or Hostname')}
                    placeholder="e.g. 192.168.1.150"
                    value={connSettings.ip}
                    onChange={(e) => setConnSettings(prev => ({ ...prev, ip: e.target.value }))}
                    helperText={t('The IP address of the machine running the Hub')}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label={t('Port')}
                    type="number"
                    value={connSettings.port}
                    onChange={(e) => setConnSettings(prev => ({ ...prev, port: e.target.value }))}
                    placeholder="5011"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 2, mt: 1, fontWeight: 700 }}>
                    {t('CONNECTION CREDENTIALS (OPTIONAL)')}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('Connection Username')}
                    value={connSettings.username}
                    onChange={(e) => setConnSettings(prev => ({ ...prev, username: e.target.value }))}
                    placeholder={t('Only if required by proxy/gateway')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type={showConnPassword ? 'text' : 'password'}
                    label={t('Connection Password')}
                    value={connPassword}
                    onChange={(e) => setConnPassword(e.target.value)}
                    placeholder={connSettings.hasPassword ? "••••••••" : t('Only if required')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowConnPassword(!showConnPassword)}
                            edge="end"
                          >
                            {showConnPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  {testResult && (
                    <Alert severity={testResult.success ? "success" : "error"} sx={{ mb: 2 }}>
                      {testResult.message}
                    </Alert>
                  )}
                  
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleTestConnection}
                      disabled={testingConnection || !connSettings.ip}
                      startIcon={testingConnection ? <CircularProgress size={20} /> : <LinkIcon />}
                    >
                      {t('Test Connection')}
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      variant="text"
                      onClick={handleResetConnection}
                      color="inherit"
                    >
                      {t('Reset to Default')}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveConnection}
                      sx={{ fontWeight: 700, px: 4 }}
                    >
                      {t('Save & Apply')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t('Current Active Endpoint')}: <strong>{CURRENT_API_URL}</strong>
                </Typography>
                {window.electron?.isElectron && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {t('Clinical Environment')}: <strong>{t('Standalone App (macOS)')}</strong>
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>
      {user?.role === 'admin' && (
        <TabPanel value={activeTab} index={2}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                {t('Patient Data Folders')}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('Configure the base directories for different PCs where patient files are stored.')}
              </Typography>

              {loading ? (
                <Box sx={{ mt: 2 }}>
                  <Skeleton height={56} sx={{ mb: 2 }} />
                  <Skeleton height={56} sx={{ mb: 2 }} />
                  <Skeleton height={40} width={160} />
                </Box>
              ) : (
                <Box sx={{ mt: 3, mb: 3 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="pc-select-label">Select PC</InputLabel>
                    <Select
                      labelId="pc-select-label"
                      value={selectedPc || ''}
                      onChange={(e) => handlePcChange(e.target.value)}
                      label="Select PC"
                      disabled={saving}
                    >
                      <MenuItem value="">
                        <em>Add new PC</em>
                      </MenuItem>
                      {folders.map((folder) => (
                        <MenuItem key={folder.id} value={folder.pc_name}>
                          {folder.pc_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {isNewPc && (
                    <TextField
                      label="PC Name"
                      value={newPcName}
                      onChange={(e) => setNewPcName(e.target.value)}
                      fullWidth
                      disabled={saving}
                      sx={{ mb: 2 }}
                      placeholder="Enter PC name (e.g., Office-PC-1)"
                    />
                  )}

                  <Box display="flex" gap={1} sx={{ mb: 2 }}>
                    <TextField
                      label="Base Folder"
                      value={folderPath}
                      onChange={(e) => setFolderPath(e.target.value)}
                      disabled={saving}
                      placeholder="Enter base folder path"
                      helperText="Example: C:/Patients or /Users/me/Documents/Patients"
                      fullWidth
                    />
                    <Button
                      variant="outlined"
                      onClick={handleBrowseFolder}
                      disabled={saving}
                      sx={{ minWidth: 100, alignSelf: 'flex-start' }}
                    >
                      Browse
                    </Button>
                  </Box>

                  <Box display="flex" gap={1} justifyContent="flex-end">
                    {!isNewPc && selectedPc && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          const folder = folders.find((f) => f.pc_name === selectedPc);
                          if (folder) handleDeleteFolder(folder);
                        }}
                        disabled={saving}
                      >
                        Delete
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={isNewPc ? handleAddFolder : handleUpdateFolder}
                      disabled={saving || !hasChanges || !folderPath.trim() || (isNewPc && !newPcName.trim())}
                    >
                      {saving ? <CircularProgress size={20} /> : isNewPc ? 'Add Folder' : 'Update Folder'}
                    </Button>
                  </Box>

                  {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                  {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
                </Box>
              )}


              {!loading && folders.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <Divider sx={{ mb: 4 }} />
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('Path Assignments')}
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="server-path-label">{t('Server path')}</InputLabel>
                        <Select
                          labelId="server-path-label"
                          value={serverPathPc}
                          onChange={(e) => {
                            setServerPathPc(e.target.value);
                            localStorage.setItem('serverPathPc', e.target.value);
                          }}
                          label={t('Server path')}
                        >
                          <MenuItem value=""><em>{t('None')}</em></MenuItem>
                          {folders.map((f) => (
                            <MenuItem key={`server-${f.id}`} value={f.pc_name}>{f.pc_name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="local-path-label">{t('Local path')}</InputLabel>
                        <Select
                          labelId="local-path-label"
                          value={localPathPc}
                          onChange={(e) => {
                            setLocalPathPc(e.target.value);
                            localStorage.setItem('localPathPc', e.target.value);
                          }}
                          label={t('Local path')}
                        >
                          <MenuItem value=""><em>{t('None')}</em></MenuItem>
                          {folders.map((f) => (
                            <MenuItem key={`local-${f.id}`} value={f.pc_name}>{f.pc_name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('Configured Folders')}
                  </Typography>
                  <List dense>
                    {folders.map((folder) => (
                      <ListItem key={folder.id} divider>
                        <ListItemText
                          primary={folder.pc_name}
                          secondary={folder.folder}
                          primaryTypographyProps={{ fontWeight: 'medium' }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            aria-label="edit"
                            onClick={() => handlePcChange(folder.pc_name)}
                            disabled={saving}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleDeleteFolder(folder)}
                            disabled={saving}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
        </TabPanel>
      )}

      {/* Tab 3: Diagnoses */}
      <TabPanel value={activeTab} index={3}>
        <DiagnosisManagement apiClient={apiClient} />
      </TabPanel>

      {/* Tab 4: Migration */}
      {user?.role === 'admin' && (
        <TabPanel value={activeTab} index={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                Data Migration (FileMaker)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Import your clinical patient records from FileMaker exports (CSV or XLSX).
                Ensure your columns follow the established mapping (e.g., ONOMA, EPITHETO, AMKA).
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input
                    accept=".csv, .xlsx, .xls"
                    style={{ display: 'none' }}
                    id="patient-import-input"
                    type="file"
                    onChange={handleFileChange}
                    disabled={importLoading}
                  />
                  <label htmlFor="patient-import-input">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      disabled={importLoading}
                    >
                      Choose File
                    </Button>
                  </label>
                  <Typography variant="body2" color="text.secondary">
                    {importFile ? importFile.name : 'No file selected'}
                  </Typography>
                </Box>

                {showMapping && availableHeaders.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DashboardIcon fontSize="small" color="primary" /> Field Binding: Map your FileMaker columns
                    </Typography>
                    <Grid container spacing={2}>
                      {MAPPABLE_FIELDS.map((field) => (
                        <Grid item xs={12} sm={6} md={4} key={field.key}>
                          <FormControl fullWidth size="small">
                            <InputLabel>{field.label}{field.required ? ' *' : ''}</InputLabel>
                            <Select
                              value={mapping[field.key] || ''}
                              onChange={(e) => handleMappingChange(field.key, e.target.value)}
                              label={field.label + (field.required ? ' *' : '')}
                            >
                              <MenuItem value="">
                                <em>-- Skip Field --</em>
                              </MenuItem>
                              {availableHeaders.map((header) => (
                                <MenuItem key={header} value={header}>
                                  {header}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                <Button
                  variant="contained"
                  onClick={handleImport}
                  disabled={!importFile || importLoading || (showMapping && MAPPABLE_FIELDS.filter(f => f.required).some(f => !mapping[f.key]))}
                  sx={{ alignSelf: 'flex-start', minWidth: 160, mt: showMapping ? 1 : 0 }}
                >
                  {importLoading ? <CircularProgress size={20} color="inherit" /> : 'Confirm & Import Patients'}
                </Button>

                {importError && (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {importError}
                  </Alert>
                )}

                {importResult && (
                  <Alert 
                    severity={importResult.failed === 0 ? "success" : "warning"}
                    sx={{ borderRadius: 2 }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Import Complete
                    </Typography>
                    <Typography variant="body2">
                      Total Rows: {importResult.total} | 
                      Successfully Imported: {importResult.success} | 
                      Failed/Skipped: {importResult.failed}
                    </Typography>
                    {importResult.errors?.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                          Recent Errors:
                        </Typography>
                        {importResult.errors.map((err, idx) => (
                          <Typography key={idx} variant="caption" sx={{ display: 'block', color: 'error.main' }}>
                            • {err}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Export Data Section */}
          {user?.role === 'admin' && (
            <Card 
              elevation={0} 
              sx={{ 
                mt: 3, 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: 3,
                bgcolor: 'action.hover'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FileDownloadIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Export Data
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Download all clinical tables (Patients, Visits, Medications, Operations, etc.) 
                  in a single organized Excel (.xlsx) file. 
                </Typography>
  
                <Button
                  variant="outlined"
                  onClick={handleExport}
                  disabled={exportLoading}
                  startIcon={exportLoading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                >
                  {exportLoading ? 'Generating Export...' : 'Export All Patient Data'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Danger Zone: Clear All Data */}
          {user?.role === 'admin' && (
            <Card 
              elevation={0} 
              sx={{ 
                mt: 3, 
                border: '1px solid', 
                borderColor: 'error.light', 
                borderRadius: 3,
                bgcolor: 'rgba(211, 47, 47, 0.02)'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WarningAmberIcon color="error" />
                  <Typography variant="h6" color="error" sx={{ fontWeight: 700 }}>
                    Danger Zone
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Permanently delete ALL patient records, clinical visits, medications, and history. 
                  This action is irreversible and usually performed before a fresh FileMaker import.
                </Typography>

                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setConfirmClearDialogOpen(true)}
                  startIcon={<DeleteIcon />}
                >
                  Clear All Patient Data
                </Button>
  
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
              </CardContent>
            </Card>
          )}
        </TabPanel>
      )}

      {/* Tab 5: Users */}
      {user?.role === 'admin' && (
        <TabPanel value={activeTab} index={5}>
          <UserManagement />
        </TabPanel>
      )}


      {/* Tab 6: Database Backup/Restore */}
      {user?.role === 'admin' && (
        <TabPanel value={activeTab} index={6}>
          <Card 
            elevation={0} 
            sx={{ 
              mb: 3, 
              border: '1px solid', 
              borderColor: 'divider', 
              borderRadius: 3,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BackupIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  System Backup
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Generate a full PostgreSQL snapshot of the entire MyEMR database. 
                This includes all patients, clinical history, users, and configurations.
              </Typography>
              <Button
                variant="contained"
                onClick={handleDownloadBackup}
                disabled={backupLoading}
                startIcon={backupLoading ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
              >
                {backupLoading ? 'Generating System Snapshot...' : 'Download Full System Backup'}
              </Button>
            </CardContent>
          </Card>

          <Card 
            elevation={0} 
            sx={{ 
              border: '1px solid', 
              borderColor: 'error.light', 
              borderRadius: 3,
              bgcolor: 'rgba(211, 47, 47, 0.02)'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <RestoreIcon color="error" />
                <Typography variant="h6" color="error" sx={{ fontWeight: 700 }}>
                  System Restoration
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Revert the entire EMR system to a previous state using a `.sql` backup file. 
                <strong>Warning: All current data will be overwritten.</strong>
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <input
                  accept=".sql"
                  style={{ display: 'none' }}
                  id="db-restore-input"
                  type="file"
                  onChange={handleRestoreFileChange}
                />
                <label htmlFor="db-restore-input">
                  <Button
                    variant="outlined"
                    color="error"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    disabled={restoreLoading}
                  >
                    Select Backup File
                  </Button>
                </label>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {restoreFile ? restoreFile.name : 'No backup selected'}
                </Typography>
              </Box>

              <Button
                variant="contained"
                color="error"
                sx={{ mt: 3 }}
                disabled={!restoreFile || restoreLoading}
                onClick={() => setConfirmRestoreDialogOpen(true)}
                startIcon={<RestoreIcon />}
              >
                Restore Database from File
              </Button>

              {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mt: 3 }}>{success}</Alert>}
            </CardContent>
          </Card>
        </TabPanel>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('Delete Folder Configuration')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove the folder configuration for{' '}
            <strong>{deleteTarget?.pc_name}</strong>? This will not delete any files.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Confirmation Dialog */}
      <Dialog 
        open={confirmClearDialogOpen} 
        onClose={() => !clearing && setConfirmClearDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmberIcon /> {t('Final Confirmation')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('This will permanently delete all patient data')}. {t('This cannot be undone')}.
          </DialogContentText>
          <Typography variant="body2" gutterBottom>
            {t('To confirm, please type DELETE ALL PATIENTS below')}:
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={clearing}
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmClearDialogOpen(false)} disabled={clearing}>
            Cancel
          </Button>
          <Button 
            onClick={handleClearAll} 
            color="error" 
            variant="contained"
            disabled={clearing || confirmText !== 'DELETE ALL PATIENTS'}
          >
            {clearing ? <CircularProgress size={24} /> : 'Delete Everything'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog 
        open={confirmRestoreDialogOpen} 
        onClose={() => !restoreLoading && setConfirmRestoreDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmberIcon /> CRITICAL: System Restoration
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, color: 'error.dark', fontWeight: 600 }}>
            YOU ARE ABOUT TO OVERWRITE THE ENTIRE CLINICAL DATABASE.
          </DialogContentText>
          <Typography variant="body2" gutterBottom>
            This will permanently replace all current records with the data from: <strong>{restoreFile?.name}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }} gutterBottom>
            To proceed, please type <strong>RESTORE SYSTEM</strong> below:
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={confirmRestoreText}
            onChange={(e) => setConfirmRestoreText(e.target.value)}
            disabled={restoreLoading}
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmRestoreDialogOpen(false)} disabled={restoreLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleRestoreDatabase} 
            color="error" 
            variant="contained"
            disabled={restoreLoading || confirmRestoreText !== 'RESTORE SYSTEM'}
          >
            {restoreLoading ? <CircularProgress size={24} color="inherit" /> : 'Confirm & Revert System'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Tab 7: System Migration Packager */}
      {user?.role === 'admin' && (
        <TabPanel value={activeTab} index={7}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                    <BackupIcon color="primary" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        System Migration Hub
                      </Typography>
                      <Typography variant="subtitle1" color="text.secondary">
                        Package your entire clinical ecosystem for Proxmox transition.
                      </Typography>
                    </Box>
                  </Box>

                  <Alert severity="info" icon={<WarningAmberIcon />} sx={{ mb: 4, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Surgical Bundle Information
                    </Typography>
                    The migration package includes your full PostgreSQL database and all patient photos/PDFs. 
                    This process may take several minutes depending on your total clinical data size. 
                    Do not close the application while generating.
                  </Alert>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
                    <Box sx={{ flex: 1, minWidth: 280, bgcolor: 'action.hover', p: 3, borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Step 1: MacBook Cleanup
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ensure all patient records are saved. Close any 3rd party database tools.
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 280, bgcolor: 'action.hover', p: 3, borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Step 2: Generate Bundle
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The system will dump the SQL and zip the 'patient-files' folder.
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 280, bgcolor: 'action.hover', p: 3, borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Step 3: Transfer to VM
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Transfer the ZIP to your Proxmox VM home folder for restoration.
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 4 }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={migrationPkgLoading ? <CircularProgress size={20} color="inherit" /> : <BackupIcon />}
                      onClick={handleDownloadMigrationPackage}
                      disabled={migrationPkgLoading}
                      sx={{ 
                        py: 2, px: 6, 
                        borderRadius: 4, 
                        fontWeight: 900, 
                        fontSize: '1.1rem',
                        boxShadow: '0 10px 20px -5px rgba(25, 118, 210, 0.4)'
                      }}
                    >
                      {migrationPkgLoading ? 'Surgery in Progress... Please Wait' : 'Generate & Download Migration Package'}
                    </Button>
                    
                    {migrationPkgLoading && (
                      <Typography variant="caption" sx={{ mt: 2, fontStyle: 'italic', color: 'primary.main' }}>
                        Compressing clinical documents and dumping database snapshot...
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      )}
    </Box>
  );
};

export default SettingsPage;
